import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { wellStatusService, WellStatus, CreateWellStatusData, UpdateWellStatusData } from '../../services/wellStatusService';

interface WellStatusState {
  wellStatuses: WellStatus[];
  isLoading: boolean;
  error: string | null;
}

const initialState: WellStatusState = {
  wellStatuses: [],
  isLoading: false,
  error: null,
};

export const fetchWellStatuses = createAsyncThunk(
  'wellStatus/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      return await wellStatusService.getAll();
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch well statuses');
    }
  }
);

export const createWellStatus = createAsyncThunk(
  'wellStatus/create',
  async (data: CreateWellStatusData, { rejectWithValue }) => {
    try {
      return await wellStatusService.create(data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create well status');
    }
  }
);

export const updateWellStatus = createAsyncThunk(
  'wellStatus/update',
  async ({ id, data }: { id: string; data: UpdateWellStatusData }, { rejectWithValue }) => {
    try {
      return await wellStatusService.update(id, data);
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update well status');
    }
  }
);

export const deleteWellStatus = createAsyncThunk(
  'wellStatus/delete',
  async (id: string, { rejectWithValue }) => {
    try {
      await wellStatusService.delete(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete well status');
    }
  }
);

const wellStatusSlice = createSlice({
  name: 'wellStatus',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchWellStatuses.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchWellStatuses.fulfilled, (state, action) => {
        state.isLoading = false;
        state.wellStatuses = action.payload;
      })
      .addCase(fetchWellStatuses.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(createWellStatus.fulfilled, (state, action) => {
        state.wellStatuses.push(action.payload);
      })
      .addCase(updateWellStatus.fulfilled, (state, action) => {
        const index = state.wellStatuses.findIndex(ws => ws._id === action.payload._id);
        if (index !== -1) {
          state.wellStatuses[index] = action.payload;
        }
      })
      .addCase(deleteWellStatus.fulfilled, (state, action) => {
        state.wellStatuses = state.wellStatuses.filter(ws => ws._id !== action.payload);
      });
  },
});

export default wellStatusSlice.reducer;
